import CONFIG from 'app.config';
import Logger from 'utils/Logger';

interface GoogleOneTapCredentialResponse {
  credential: string;
}

interface GoogleOneTapOptions {
  autoPrompt: boolean;
  cancelOnTapOutside: boolean;
  context: string;
}

type GoogleOneTapAuthenticationSuccessCallback = (isNewUser: boolean) => void;

class GoogleOneTapService {
  static #isInitialized = false;
  static #onAuthenticationSuccess: GoogleOneTapAuthenticationSuccessCallback | null = null;

  static setAuthenticationSuccessCallback(callback: GoogleOneTapAuthenticationSuccessCallback): void {
    GoogleOneTapService.#onAuthenticationSuccess = callback;
  }

  static async initialize(): Promise<void> {
    if (GoogleOneTapService.#isInitialized) {
      return;
    }

    const clientId = CONFIG.GOOGLE_ONE_TAP_CLIENT_ID;

    if (!clientId) {
      Logger.error('Google One Tap: Client ID not configured.');
      return;
    }

    try {
      await GoogleOneTapService.#initializeGoogleOneTapInternal({
        autoPrompt: true,
        cancelOnTapOutside: true,
        context: 'signin',
      });
    } catch (error) {
      Logger.error('Google One Tap: Could not initialize', { error });
    }
  }

  static async #initializeGoogleOneTapInternal(options: GoogleOneTapOptions): Promise<void> {
    GoogleOneTapService.#isInitialized = true;

    if (window.google?.accounts?.id) {
      await GoogleOneTapService.#onGoogleOneTapScriptLoaded(options);
      return;
    }

    await GoogleOneTapService.#loadGoogleOneTapScript(options);
  }

  static async #loadGoogleOneTapScript(options: GoogleOneTapOptions): Promise<void> {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;

    return new Promise((resolve, reject) => {
      script.onload = async () => {
        try {
          await GoogleOneTapService.#onGoogleOneTapScriptLoaded(options);
          resolve();
        } catch (error) {
          reject(error);
        }
      };

      script.onerror = () => {
        Logger.error('Failed to load Google One Tap SDK');
        GoogleOneTapService.#isInitialized = false;
        reject(new Error('Failed to load Google One Tap SDK'));
      };

      document.head.appendChild(script);
    });
  }

  static async #handleCredentialResponse(response: GoogleOneTapCredentialResponse): Promise<void> {
    if (!response.credential) {
      Logger.error('Google One Tap: No credential received');
      return;
    }

    try {
      const authResponse = await fetch(`${CONFIG.API_URL}/auth/google-one-tap/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credential: response.credential,
        }),
        credentials: 'include',
      });

      if (!authResponse.ok) {
        Logger.error('Google One Tap authentication failed', {
          status: authResponse.status,
          statusText: authResponse.statusText,
        });
        return;
      }

      const { isNewUser } = await authResponse.json();

      if (GoogleOneTapService.#onAuthenticationSuccess) {
        GoogleOneTapService.#onAuthenticationSuccess(isNewUser);
      }
    } catch (error) {
      Logger.error('Google One Tap authentication failed', { error });
    }
  }

  static async #onGoogleOneTapScriptLoaded(options: GoogleOneTapOptions): Promise<void> {
    if (!window.google?.accounts?.id) {
      Logger.error('Google One Tap SDK failed to load');
      return;
    }

    try {
      window.google.accounts.id.initialize({
        client_id: CONFIG.GOOGLE_ONE_TAP_CLIENT_ID,
        callback: GoogleOneTapService.#handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: options.cancelOnTapOutside,
        context: options.context,
        ux_mode: 'popup',
        use_fedcm_for_prompt: false,
      });

      if (options.autoPrompt) {
        window.google.accounts.id.prompt();
      }
    } catch (error) {
      Logger.error('Failed to initialize Google One Tap', { error });
    }
  }
}

export default GoogleOneTapService;
